import { API_ENDPOINTS } from '@/api/endpoints';
import { energyRateEndpoints } from '@/api/endpoints/energyRates';
import { apiFetch } from '@/util/apiClient';
import { useQuery } from '@tanstack/react-query';
import {
  ElectricRateCard,
  ExternalEnergy,
  GasRateCard,
  OnsiteEnergy,
} from '../types';

export const useOnsiteEnergyQuery = () => {
  return useQuery({
    queryKey: ['energy', 'onsite'],
    queryFn: () => apiFetch<OnsiteEnergy[]>(API_ENDPOINTS.COMMON.energy.onsite),
    staleTime: 5 * 60 * 1000,
  });
};

export const useExternalEnergyQuery = () => {
  return useQuery({
    queryKey: ['energy', 'external'],
    queryFn: () =>
      apiFetch<ExternalEnergy[]>(API_ENDPOINTS.COMMON.energy.external),
    staleTime: 5 * 60 * 1000,
  });
};

interface RateQueryResponse<T> {
  results: T[];
  warning: string | null;
}

/**
 * The rate endpoints answer 200 with `results: []` and a `warning` when the
 * project's utility territory cannot be resolved (no mapped shape, no cached
 * utility ids, a failed service-area lookup). That means "unknown", not "no
 * plans". It is surfaced as a query ERROR so no caller can mistake it for a
 * loaded, empty list -- the energy screen strips selected plans that are not in
 * a loaded list (decarb4-chain-activation 012).
 */
export class UnresolvedRatesError extends Error {}

const unwrapRates = <T>(res: RateQueryResponse<T>): T[] => {
  if (res.warning) {
    throw new UnresolvedRatesError(res.warning);
  }
  return res.results;
};

// An unresolvable territory is an answer, not a transient failure: no retries.
const retryUnlessUnresolved = (failureCount: number, error: Error) =>
  !(error instanceof UnresolvedRatesError) && failureCount < 3;

export const useElectricRatesQuery = (
  orgId: string | undefined,
  projectId: string | undefined,
  subprojectId?: string,
) =>
  useQuery({
    queryKey: ['electricRates', orgId, projectId, subprojectId],
    queryFn: async () => {
      const res = await apiFetch<RateQueryResponse<ElectricRateCard>>(
        energyRateEndpoints.ELECTRIC_RATES(orgId!, projectId!, {
          subprojectId,
        }),
      );
      return unwrapRates(res);
    },
    retry: retryUnlessUnresolved,
    enabled: !!orgId && !!projectId,
    staleTime: 5 * 60 * 1000,
  });

export const useGasRatesQuery = (
  orgId: string | undefined,
  projectId: string | undefined,
  subprojectId?: string,
) =>
  useQuery({
    queryKey: ['gasRates', orgId, projectId, subprojectId],
    queryFn: async () => {
      const res = await apiFetch<RateQueryResponse<GasRateCard>>(
        energyRateEndpoints.GAS_RATES(orgId!, projectId!, { subprojectId }),
      );
      return unwrapRates(res);
    },
    retry: retryUnlessUnresolved,
    enabled: !!orgId && !!projectId,
    staleTime: 5 * 60 * 1000,
  });
